import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  Buildings, Users, ClipboardText, ChatCircleDots,
  ShieldStar, ArrowRight, Megaphone, UsersThree, ListChecks,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AnnouncementDetail, AnnouncementsAllSheet } from "../components/AnnouncementModals";

const TILES = [
  { to: "/visitors", label: "Visitors", sub: "Log entry & approvals", Icon: UsersThree, accent: "cyan", moduleKey: "visitors" },
  { to: "/flats", label: "Flat Directory", sub: "Look up any flat", Icon: Buildings, accent: "cyan", moduleKey: "flats" },
  { to: "/tenants", label: "Tenants & Owners", sub: "Residents across wings", Icon: Users, accent: "lime", moduleKey: "tenants" },
  { to: "/staff", label: "Attendance", sub: "Mark security & housekeeping", Icon: ClipboardText, accent: "cyan", moduleKey: "attendance" },
];

export default function SecurityHome() {
  const { user, isFeatureEnabled } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/api/announcements").then(({ data }) => setItems(data.items)).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const visibleAnn = useMemo(() => items.slice(0, 3), [items]);

  return (
    <div className="space-y-7 font-sans" data-testid="security-home">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">{greet}</p>
          <h1 className="font-display text-3xl font-extrabold leading-tight text-white">
            {user?.name?.split(" ")[0] || "Security"} <span className="text-neon-cyan">·</span>
          </h1>
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-white/60">
            <ShieldStar size={14} weight="fill" className="text-neon-cyan" /> Security desk · Silver Altire
          </p>
        </div>
        <button onClick={() => navigate("/account")}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/[0.05] font-display text-base font-bold text-white">
          {user?.name?.[0]?.toUpperCase() || "S"}
        </button>
      </div>

      {/* Daily Checklist — same prominent button as the Society Manager home */}
      {isFeatureEnabled("checklist") && (
        <button onClick={() => navigate("/checklist")} className="btn-primary w-full" data-testid="security-daily-checklist">
          <ListChecks size={16} weight="fill" className="mr-1.5" /> Daily Checklist
        </button>
      )}

      {/* Operations tiles */}
      <div className="grid grid-cols-2 gap-3">
        {TILES.filter((t) => isFeatureEnabled(t.moduleKey)).map(({ to, label, sub, Icon, accent }) => {
          const bg = accent === "lime" ? "bg-neon-lime shadow-glow-lime" : "bg-neon-cyan shadow-glow-cyan";
          return (
            <button key={to} onClick={() => navigate(to)} data-testid={`sec-tile-${to.replace(/\//g, "")}`}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-white/20 active:scale-[0.98]">
              <div className={`mb-8 inline-flex h-10 w-10 items-center justify-center rounded-2xl ${bg} text-black`}><Icon size={20} weight="bold" /></div>
              <p className="font-display text-base font-semibold text-white">{label}</p>
              <p className="text-[11px] text-white/55">{sub}</p>
              <ArrowRight weight="bold" className="absolute bottom-4 right-4 text-white/40 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
            </button>
          );
        })}
      </div>

      {/* Message manager */}
      {isFeatureEnabled("chat") && (
        <button onClick={() => navigate("/chat")}
          className="card flex w-full items-center gap-3 p-4 text-left transition-all hover:border-white/20 active:scale-[0.99]">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-neon-cyan text-black shadow-glow-cyan"><ChatCircleDots size={20} weight="bold" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[15px] font-semibold text-white">Message Society Office</p>
            <p className="text-[12px] text-white/55">Chat with the manager and residents</p>
          </div>
          <ArrowRight weight="bold" className="text-white/35" />
        </button>
      )}

      {/* Announcements */}
      {isFeatureEnabled("announcements") && (
      <div>
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-lg font-bold text-white">Announcements</h2>
          {items.length > 0 && (
            <button data-testid="ann-see-all" onClick={() => setShowAll(true)}
              className="text-[11px] font-bold uppercase tracking-wider text-neon-cyan">See all</button>
          )}
        </div>
        {visibleAnn.length === 0 ? (
          <div className="card p-5 text-center text-[13px] text-white/45">No announcements yet.</div>
        ) : (
          <div className="space-y-2.5">
            {visibleAnn.map((a) => (
              <button key={a.id} data-testid={`announcement-${a.id}`} onClick={() => setSelected(a)}
                className="card w-full p-4 text-left hover:border-white/20">
                <div className="mb-1 flex items-center gap-2">
                  <Megaphone size={14} weight="fill" className="text-neon-lime" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-neon-lime">{a.tag}</span>
                </div>
                <p className="font-display text-[15px] font-semibold leading-snug text-white">{a.title}</p>
                <p className="mt-1 line-clamp-1 text-[13px] leading-relaxed text-white/60">{a.body}</p>
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      <AnimatePresence>
        {selected && <AnnouncementDetail a={selected} onClose={() => setSelected(null)} />}
        {showAll && <AnnouncementsAllSheet items={items} onClose={() => setShowAll(false)} onOpen={(a) => { setShowAll(false); setSelected(a); }} />}
      </AnimatePresence>
    </div>
  );
}
